import axios from 'axios';
import AuthService from '@/services/AuthService';

const CHANGE_REQUEST_TYPES = {
  COMPANY_INFORMATION: 'companyDetails.register',
  EMPLOYEES: 'companyDetails.employees',
  INSURANCE_PUBLIC_LIABILITY: 'companyDetails.insurance.publicLiabilityInsurance',
  INSURANCE_WORKERS_COMPENSATION: 'companyDetails.insurance.workersCompensationInsurance',
  VOTING_MEMBERS: 'companyDetails.votingMembers',
  DIRECTORS: 'companyDetails.directors',
  DIRECTORS_ADD: 'companyDetails.directors.add',
  DIRECTORS_CHANGE: 'companyDetails.directors.change',
  DIRECTORS_REMOVE: 'companyDetails.directors.remove',
  LICENSES_WATERWISE: 'companyDetails.licenses.waterwise',
  LICENSES_WATERWISE_REMOVE: 'companyDetails.licenses.waterwise.remove',
  LICENSES_PLUMBING_CONTRACTOR: 'companyDetails.licenses.plumbingcontractor',
  LICENSES_GAS: 'companyDetails.licenses.gas',
  LICENSES_BACKFLOW: 'companyDetails.licenses.backflow',
  LICENSES_NREL_CONTRACTOR: 'companyDetails.licenses.nrelcontractor',
  LICENSES_ADDITIONAL_EDIT: 'companyDetails.licenses.additional.edit',
  LICENSES_ADDITIONAL_ADD: 'companyDetails.licenses.additional.add',
  LICENSES_ADDITIONAL_REMOVE: 'companyDetails.licenses.additional.remove',
};
Object.freeze(CHANGE_REQUEST_TYPES);

const baseUrl = `${process.env.VUE_APP_API_BASEURL}/VotingChangeRequests`;
const ADMIN_IMMEDIATE_APPROVAL_TYPES = new Set([
  CHANGE_REQUEST_TYPES.COMPANY_INFORMATION,
  CHANGE_REQUEST_TYPES.LICENSES_PLUMBING_CONTRACTOR,
  CHANGE_REQUEST_TYPES.LICENSES_GAS,
  CHANGE_REQUEST_TYPES.LICENSES_BACKFLOW,
  CHANGE_REQUEST_TYPES.LICENSES_NREL_CONTRACTOR,
  CHANGE_REQUEST_TYPES.LICENSES_ADDITIONAL_EDIT,
  CHANGE_REQUEST_TYPES.LICENSES_ADDITIONAL_REMOVE,
  CHANGE_REQUEST_TYPES.LICENSES_ADDITIONAL_ADD,
  CHANGE_REQUEST_TYPES.LICENSES_WATERWISE,
  CHANGE_REQUEST_TYPES.LICENSES_WATERWISE_REMOVE,
  CHANGE_REQUEST_TYPES.EMPLOYEES,
  CHANGE_REQUEST_TYPES.INSURANCE_PUBLIC_LIABILITY,
  CHANGE_REQUEST_TYPES.INSURANCE_WORKERS_COMPENSATION,
]);

export const isAdminImmediateApprovalChangeType = (changeTypeId) => {
  return !!changeTypeId && ADMIN_IMMEDIATE_APPROVAL_TYPES.has(changeTypeId);
};

export const requiresVoterApproval = (changeTypeId) => {
  return !isAdminImmediateApprovalChangeType(changeTypeId);
};

export default {
  CHANGE_REQUEST_TYPES,

  getHeaders() {
    const _headers = {
      'Content-Type': 'application/json',
      Authorization: `${AuthService.getAuthHeader()}`,
    };
    return _headers;
  },

  handleChangeType(item) {
    if (!item) {
      return;
    }

    switch (item.changeTypeId) {
      case CHANGE_REQUEST_TYPES.LICENSES_PLUMBING_CONTRACTOR:
        item.changeType = 'Plumbing Contractor Licence';
        break;

      case CHANGE_REQUEST_TYPES.LICENSES_GAS:
        item.changeType = 'Gas Licence';
        break;

      case CHANGE_REQUEST_TYPES.LICENSES_BACKFLOW:
        item.changeType = 'Backflow Licence';
        break;

      case CHANGE_REQUEST_TYPES.LICENSES_NREL_CONTRACTOR:
        item.changeType = 'NREL Contractor Licence';
        break;

      case CHANGE_REQUEST_TYPES.LICENSES_ADDITIONAL_EDIT:
        item.changeType = 'Updating Additional Licence';
        break;

      case CHANGE_REQUEST_TYPES.LICENSES_ADDITIONAL_REMOVE:
        item.changeType = 'Remove Additional Licence';
        break;

      case CHANGE_REQUEST_TYPES.LICENSES_ADDITIONAL_ADD:
        item.changeType = 'Adding Additional Licence';
        break;

      case CHANGE_REQUEST_TYPES.LICENSES_WATERWISE:
        item.changeType = 'Updating Waterwise Licence';
        break;

      case CHANGE_REQUEST_TYPES.LICENSES_WATERWISE_REMOVE:
        item.changeType = 'Remove Waterwise Licence';
        break;

      case CHANGE_REQUEST_TYPES.EMPLOYEES:
        item.changeType = 'Employees';
        break;

      case CHANGE_REQUEST_TYPES.VOTING_MEMBERS:
        item.changeType = 'Voting Member';
        break;

      // case CHANGE_REQUEST_TYPES.DIRECTORS:
      //   item.changeType = 'Directors';
      //   break;

      case CHANGE_REQUEST_TYPES.DIRECTORS_ADD:
        item.changeType = 'Add Director';
        break;

      case CHANGE_REQUEST_TYPES.DIRECTORS_CHANGE:
        item.changeType = 'Change Director';
        break;

      case CHANGE_REQUEST_TYPES.DIRECTORS_REMOVE:
        item.changeType = 'Remove Director';
        break;

      case CHANGE_REQUEST_TYPES.COMPANY_INFORMATION:
        item.changeType = 'Company Information';
        break;

      case CHANGE_REQUEST_TYPES.INSURANCE_PUBLIC_LIABILITY:
        item.changeType = 'Public Liability Insurance';
        break;

      case CHANGE_REQUEST_TYPES.INSURANCE_WORKERS_COMPENSATION:
        item.changeType = 'Workers Compensation Insurance';
        break;

      default:
        item.changeType = 'Unknown';
        break;
    }
  },

  normalizeResponseData(data) {
    if (!data) {
      return data;
    }

    if (Array.isArray(data)) {
      return data.map((item) => {
        if (typeof item.userActions === 'string') {
          item.userActions = JSON.parse(item.userActions || '[]');
        }
        this.handleChangeType(item);
        return item;
      });
    } else if (typeof data === 'object') {
      if (typeof data.userActions === 'string') {
        data.userActions = JSON.parse(data.userActions || '[]');
      }
      this.handleChangeType(data);
    }

    return data;
  },

  getChangeRequests(status, options = {}) {
    const url = status ? `${baseUrl}?status=${status}` : `${baseUrl}`;
    const requestConfig = {
      headers: this.getHeaders(),
    };
    if (options.signal) {
      requestConfig.signal = options.signal;
    }
    return new Promise((resolve, reject) => {
      return (
        axios
          .get(url, requestConfig)
          // get data
          .then((resp) => {
            if (resp.data?.data?.changeRequests) {
              const d = this.normalizeResponseData(resp.data.data.changeRequests);
              resolve(d);
            } else {
              resolve([]);
            }
          })
          .catch((error) => {
            reject(error);
          })
      );
    });
  },

  getChangeRequest(id) {
    const url = `${baseUrl}/${id}`;
    return new Promise((resolve, reject) => {
      return (
        axios
          .get(url, { headers: this.getHeaders() })
          // get data
          .then((resp) => {
            if (resp.data?.data?.changeRequest) {
              resolve(this.normalizeResponseData(resp.data.data.changeRequest));
            } else {
              resolve(null);
            }
          })
          .catch((error) => {
            reject(error);
          })
      );
    });
  },

  verifyChangeRequest(id) {
    const url = `${baseUrl}/${id}/verify`;
    return new Promise((resolve, reject) => {
      return axios
        .post(url, null, { headers: this.getHeaders() })
        .then((resp) => {
          if (resp.data?.data?.changeRequest) {
            resolve(this.normalizeResponseData(resp.data.data.changeRequest));
          } else {
            resolve(null);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  approveChangeRequest(id) {
    const url = `${baseUrl}/${id}/approve`;
    return new Promise((resolve, reject) => {
      return axios
        .post(url, null, { headers: this.getHeaders() })
        .then((resp) => {
          if (resp.data?.data?.changeRequest) {
            resolve(this.normalizeResponseData(resp.data.data.changeRequest));
          } else {
            resolve(null);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  denyChangeRequest(id) {
    const url = `${baseUrl}/${id}/deny`;
    return new Promise((resolve, reject) => {
      return axios
        .post(url, null, { headers: this.getHeaders() })
        .then((resp) => {
          if (resp.data?.data?.changeRequest) {
            resolve(this.normalizeResponseData(resp.data.data.changeRequest));
          } else {
            resolve(null);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
};
